/**
 * `devpulse://…` opened elsewhere on the machine, turned into a page.
 *
 * The shell hands over the whole URL and reads nothing out of it, so what a link means is decided
 * here — in the place that already knows what a route is. Today one thing is understood:
 *
 *     devpulse://session/<id>     the session's screen
 *
 * Anything else is ignored rather than guessed at. A link is somebody else's input, and the app it
 * arrives in can read a repository.
 *
 * In a browser this does nothing at all.
 */

import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { onOpenUrl } from './desktop'

/** Where a link points, or null when it is not one we serve. */
export function routeForLink(url: string): string | null {
  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return null
  }

  if (parsed.protocol !== 'devpulse:') return null

  // devpulse://session/<id> — the host is "session" and the id is the path.
  if (parsed.hostname === 'session') {
    const id = parsed.pathname.replace(/^\/+/, '')
    return /^[0-9a-f-]{36}$/i.test(id) ? `/work/${id}` : null
  }

  return null
}

export function useDesktopLinks(): void {
  const navigate = useNavigate()

  useEffect(() => {
    let stop: (() => void) | undefined
    let dropped = false

    void onOpenUrl((urls) => {
      for (const url of urls) {
        const route = routeForLink(url)
        if (route) {
          navigate(route)
          return
        }
      }
    }).then((off) => {
      // The component may have gone while the listener was being set up.
      if (dropped) off()
      else stop = off
    })

    return () => {
      dropped = true
      stop?.()
    }
  }, [navigate])
}
